import {
  UserModel,
  WebAuthnAuthenticateBeginResponse,
  WebAuthnAuthenticateFinishResponse,
  WebAuthnRegisterBeginResponse,
  WebAuthnRegisterFinishResponse,
  prepareCredentialResponse
} from '@aionzdev/modules';
import { HttpClient } from '@angular/common/http';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Usuario } from '../../models/usuario';

export interface LoginResponse {
  tokenAplicacao: string;
  refreshToken: string;
  user: UserModel;
}

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly _http = inject(HttpClient);
  private readonly _document = inject(DOCUMENT);
  private readonly baseUrl = `${environment.apiUrl}`;

  login(email: string, senha: string): Observable<Usuario> {
    return this._http.post<Usuario>(`${this.baseUrl}/auth/login`, { email, senha });
  }

  requestPassword(email: string): Observable<void> {
    const url = this._document.defaultView?.window.location.hostname;

    return this._http.post<void>(`${this.baseUrl}/auth/forgot`, { email, url });
  }

  resetPassword(idUsuario: string, hash: string, newPassword: string): Observable<void> {
    return this._http.post<void>(`${this.baseUrl}/auth/updatePassword`, {
      id: idUsuario,
      hash,
      senha: newPassword
    });
  }

  register(params: {
    name: string;
    userName: string;
    password: string;
    phone?: string;
    email?: string;
  }): Observable<void> {
    return this._http.post<void>(`${this.baseUrl}/register`, params);
  }

  getUserByHash(hash: string): Observable<Usuario> {
    return this._http.get<Usuario>(`${this.baseUrl}/auth/findUsuarioByHash/${hash}`);
  }

  // GET /auth/me — usuário do token, sem parâmetro. Retorno idêntico ao antigo
  // GET /user/findByMail?email= (mesmo findOne + attachEmpresaPadrao no backend).
  getMe(): Observable<Usuario> {
    return this._http.get<Usuario>(`${this.baseUrl}/auth/me`);
  }

  refreshToken(refreshToken: string): Observable<Usuario> {
    return this._http.post<Usuario>(`${this.baseUrl}/auth/refresh`, { refreshToken });
  }

  startWebAuthnRegistration(rpName: string, rpID: string): Observable<WebAuthnRegisterBeginResponse> {
    return this._http.post<WebAuthnRegisterBeginResponse>(`${this.baseUrl}/auth/webauthn/register/begin`, {
      rpName,
      rpID
    });
  }

  finishWebAuthnRegistration(params: {
    response: PublicKeyCredential;
    credentialName?: string;
    origin: string;
    rpID: string;
  }): Observable<WebAuthnRegisterFinishResponse> {
    return this._http.post<WebAuthnRegisterFinishResponse>(`${this.baseUrl}/auth/webauthn/register/finish`, {
      response: prepareCredentialResponse(params.response),
      credentialName: params.credentialName,
      origin: params.origin,
      rpID: params.rpID
    });
  }

  startWebAuthnAuthentication(rpID: string): Observable<WebAuthnAuthenticateBeginResponse> {
    return this._http.post<WebAuthnAuthenticateBeginResponse>(`${this.baseUrl}/auth/webauthn/authenticate/begin`, {
      rpID
    });
  }

  finishWebAuthnAuthentication(params: {
    response: PublicKeyCredential;
    origin: string;
    rpID: string;
  }): Observable<WebAuthnAuthenticateFinishResponse> {
    return this._http.post<WebAuthnAuthenticateFinishResponse>(
      `${this.baseUrl}/auth/webauthn/authenticate/finish`,
      {
        response: prepareCredentialResponse(params.response),
        origin: params.origin,
        rpID: params.rpID
      }
    );
  }
}
